/**
 * RoleBulkEvalJobCard.jsx — Slice β operator visibility surface.
 *
 * Renders the live state of a bulk-evaluation job for one role.
 * Self-rehydrates from the sessionStorage runMarker so a refresh
 * or tab-return immediately re-binds to the in-flight UI.
 *
 * Inputs:
 *   roleId        — required; scopes the runMarker
 *   activeJobId   — optional override (passed by parent right after
 *                   dispatch so we don't need to wait for marker
 *                   storage round-trip)
 *   onTerminal    — invoked once when the job reaches a terminal
 *                   status; parent uses this to refresh the
 *                   candidate list
 *
 * Renders nothing when there is neither an activeJobId nor a marker.
 *
 * Colour doctrine (Slice α / Phase 1.6 compliance):
 *   queued/running    blue
 *   complete          emerald
 *   partial           amber
 *   failed            rose
 *   neutral copy      slate
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RotateCw, X, CheckCircle2, AlertTriangle, Activity,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { playCompletionChime } from '@/lib/completionChime';
import { pollBulkEvalJob } from '@/lib/employerBulkPolling';
import {
  getMarker, clearMarker,
} from '@/lib/employerBulkRunMarker';


function statusPill(status) {
  const map = {
    queued:   { cls: 'border-blue-500/40 text-blue-300 bg-blue-500/10',         label: 'Queued',   icon: Activity },
    running:  { cls: 'border-blue-500/40 text-blue-300 bg-blue-500/10',         label: 'Running',  icon: Activity },
    complete: { cls: 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10', label: 'Complete', icon: CheckCircle2 },
    partial:  { cls: 'border-amber-500/40 text-amber-300 bg-amber-500/10',       label: 'Partial',  icon: AlertTriangle },
    failed:   { cls: 'border-rose-500/40 text-rose-300 bg-rose-500/10',          label: 'Failed',   icon: AlertTriangle },
  };
  const cfg = map[status] || map.queued;
  const Icon = cfg.icon;
  return (
    <Badge
      variant="outline"
      className={`${cfg.cls} text-[10px] uppercase tracking-wider flex items-center gap-1`}
      data-testid="role-bulk-eval-job-status-pill"
      data-job-status={status}
    >
      <Icon className="h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}


function formatMoney(amount_cents, currency) {
  if (amount_cents == null) return '—';
  const v = (Number(amount_cents) || 0) / 100;
  const sym = currency === 'GBP' ? '£'
            : currency === 'USD' ? '$'
            : currency === 'EUR' ? '€'
            : `${currency || ''} `;
  return `${sym}${v.toFixed(2)}`;
}


function summariseItems(items) {
  if (!Array.isArray(items)) return { running: 0, terminal: 0, total: 0 };
  let running = 0;
  let terminal = 0;
  items.forEach((it) => {
    if (it.item_status === 'queued' || it.item_status === 'running') running += 1;
    else terminal += 1;
  });
  return { running, terminal, total: items.length };
}


export default function RoleBulkEvalJobCard({ roleId, activeJobId, onTerminal }) {
  const [jobId, setJobId]       = useState(activeJobId || null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [error, setError]       = useState(null);
  const abortRef                = useRef(null);
  const terminalFiredRef        = useRef(false);

  // Rehydrate from runMarker on mount + when parent forces activeJobId.
  useEffect(() => {
    if (activeJobId) {
      setJobId(activeJobId);
      return;
    }
    const m = getMarker(roleId);
    if (m?.jobId) setJobId(m.jobId);
  }, [activeJobId, roleId]);

  // Polling lifecycle.
  useEffect(() => {
    if (!jobId) return undefined;
    terminalFiredRef.current = false;
    const ac = new AbortController();
    abortRef.current = ac;
    setBusy(true);
    setError(null);

    pollBulkEvalJob(jobId, {
      signal: ac.signal,
      onTick: (snap) => {
        setSnapshot(snap);
      },
    })
      .then((terminal) => {
        setSnapshot(terminal);
        if (!terminalFiredRef.current) {
          terminalFiredRef.current = true;
          // Long-running completion notification only.
          // Bulk-eval has THREE terminal states; only "complete" is
          // a successful finish. Skip the chime on partial / failed.
          if (terminal?.job?.status === 'complete') {
            try { playCompletionChime(); } catch (_) { /* silent */ }
          }
          onTerminal?.(terminal);
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setError(err?.message || 'Polling failed.');
      })
      .finally(() => setBusy(false));

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const job = snapshot?.job;
  const items = snapshot?.items;
  const status = job?.status;
  const isTerminal = useMemo(
    () => status === 'complete' || status === 'partial' || status === 'failed',
    [status],
  );
  const itemsSummary = useMemo(() => summariseItems(items), [items]);
  const canRetry = isTerminal
    && Number(job?.failed_count || 0) + itemsSummary.total > 0
    && (
      Number(job?.failed_count || 0) > 0
      || (Array.isArray(items) && items.some((it) => it.item_status === 'failed' || it.item_status === 'timeout'))
    );

  const onResume = useCallback(async () => {
    if (!jobId) return;
    setResumeBusy(true);
    setError(null);
    try {
      await apiFetch(
        `/api/employer/v1/bulk-eval/jobs/${jobId}/resume`,
        { method: 'POST' },
      );
      toast.success('Bulk evaluation resumed.');
      // Restart polling by re-triggering the effect with the same jobId.
      terminalFiredRef.current = false;
      setSnapshot(null);
      // Force re-poll by toggling jobId via a no-op update — easiest
      // is to set the same value via functional set; React schedules
      // re-run because we depend on it.
      setJobId((id) => id);
      const ac = new AbortController();
      abortRef.current?.abort();
      abortRef.current = ac;
      setBusy(true);
      pollBulkEvalJob(jobId, {
        signal: ac.signal,
        onTick: setSnapshot,
      }).then((terminal) => {
        setSnapshot(terminal);
        if (!terminalFiredRef.current) {
          terminalFiredRef.current = true;
          onTerminal?.(terminal);
        }
      }).catch((err) => {
        if (err?.name !== 'AbortError') setError(err?.message || 'Polling failed.');
      }).finally(() => setBusy(false));
    } catch (err) {
      const detail = err?.data?.detail || err?.data || {};
      const code = detail.code || err?.code || 'RESUME_FAILED';
      const msg  = detail.message || err?.message || 'Resume failed.';
      toast.error(`${code}: ${msg}`);
      setError(`${code}: ${msg}`);
    } finally {
      setResumeBusy(false);
    }
  }, [jobId, onTerminal]);

  const onDismiss = useCallback(() => {
    abortRef.current?.abort();
    clearMarker(roleId);
    setJobId(null);
    setSnapshot(null);
    setError(null);
  }, [roleId]);

  if (!jobId) {
    return (
      <Card
        className="border-ndor-edge bg-ndor-surface"
        data-testid="role-bulk-eval-job-card-empty"
      >
        <CardContent className="p-4 flex items-start gap-3">
          <div className="rounded-md border border-slate-500/40 bg-slate-500/10 p-2 shrink-0">
            <Activity className="h-4 w-4 text-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ndor-ink">No bulk evaluation in flight</p>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Launch a bulk run from the Candidates &amp; Dossiers panel to score every parsed dossier against this role at once. Live progress, per-candidate verdicts and the charged total will appear here.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid="role-bulk-eval-job-card"
      data-job-id={jobId}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">
              Bulk evaluation
            </p>
            {status ? statusPill(status) : (
              <Badge variant="outline" className="border-slate-500/40 text-slate-300 bg-slate-500/10 text-xs uppercase tracking-wider">
                Loading…
              </Badge>
            )}
            {busy && !isTerminal && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-300" />
            )}
          </div>
          {isTerminal && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-slate-300 hover:text-ndor-ink"
              aria-label="Dismiss"
              data-testid="role-bulk-eval-job-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {job && (
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]"
            data-testid="role-bulk-eval-job-counters"
          >
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5">
              <p className="uppercase tracking-wider text-emerald-300/80">Complete</p>
              <p className="text-base font-semibold text-emerald-300">
                {job.complete_count ?? 0}
              </p>
            </div>
            <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1.5">
              <p className="uppercase tracking-wider text-rose-300/80">Failed</p>
              <p className="text-base font-semibold text-rose-300">
                {job.failed_count ?? 0}
              </p>
            </div>
            <div className="rounded-md border border-slate-500/40 bg-slate-500/10 px-2 py-1.5">
              <p className="uppercase tracking-wider text-slate-300/80">Skipped</p>
              <p className="text-base font-semibold text-slate-300">
                {job.skipped_count ?? 0}
              </p>
            </div>
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5">
              <p className="uppercase tracking-wider text-amber-300/80">Timeout</p>
              <p className="text-base font-semibold text-amber-300">
                {Array.isArray(items)
                  ? items.filter((it) => it.item_status === 'timeout').length
                  : 0}
              </p>
            </div>
          </div>
        )}

        {Array.isArray(items) && items.length > 0 && (
          <div className="text-xs text-slate-300" data-testid="role-bulk-eval-job-items-summary">
            {itemsSummary.terminal} of {itemsSummary.total} candidates processed
            {!isTerminal && itemsSummary.running > 0 && ` · ${itemsSummary.running} in flight`}
          </div>
        )}

        {isTerminal && Number(job?.charged_total_cents || 0) > 0 && (
          <div
            className="text-xs text-slate-300"
            data-testid="role-bulk-eval-job-charged"
          >
            Charged total: {formatMoney(job.charged_total_cents, job.currency_snapshot)} ·
            recorded for later invoicing or billing reconciliation
          </div>
        )}

        {error && (
          <div className="text-xs text-rose-400 flex items-start gap-2" data-testid="role-bulk-eval-job-error">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {isTerminal && (
          <div className="flex items-center justify-end gap-2 pt-1">
            {canRetry && (
              <Button
                onClick={onResume}
                disabled={resumeBusy}
                size="sm"
                className="h-7 text-[11px] bg-blue-500 hover:bg-blue-500/90 text-white"
                data-testid="role-bulk-eval-job-resume-btn"
              >
                {resumeBusy ? (
                  <><Loader2 className="mr-1 h-3 w-3 animate-spin" />Resuming…</>
                ) : (
                  <><RotateCw className="mr-1 h-3 w-3" />Retry failed</>
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
